// plugins/error-handler.client.js
export default defineNuxtPlugin((nuxtApp) => {
  // List of errors that should only log to console (not show error page)
  const nonFatalErrorPatterns = [
    /navigation guard/i,
    /vue router warn/i,
    /hydration/i,
    /key attribute/i,
    /duplicate keys/i,
    /invalid prop/i,
    /failed prop validation/i,
    /avoid mutating a prop/i,
    /component.*is missing/i,
    /slot.*not found/i,
  ];

  const shouldShowErrorPage = (error) => {
    const errorMessage = error?.message || String(error);

    // Don't show error page for non-fatal patterns
    if (nonFatalErrorPatterns.some((pattern) => pattern.test(errorMessage))) {
      return false;
    }

    // Show error page for these critical cases
    const fatalPatterns = [
      /cannot read propert.*of undefined/i,
      /cannot read propert.*of null/i,
      /is not a function/i,
      /failed to fetch/i,
      /network error/i,
      /chunk load.*failed/i,
      /unexpected token/i,
      /syntaxerror/i,
    ];

    return fatalPatterns.some((pattern) => pattern.test(errorMessage));
  };

  // Handle Vue errors
  nuxtApp.vueApp.config.errorHandler = (error, instance, info) => {
    console.error('Vue Error:', error, info);

    // Only show error page for critical errors
    if (shouldShowErrorPage(error)) {
      showError({
        statusCode: 500,
        statusMessage: 'Internal Server Error',
        message: import.meta.dev ? error.message : 'Something went wrong',
        fatal: true,
      });
    }
  };

  // Vue error hook
  nuxtApp.hook('vue:error', (error, instance, info) => {
    console.error('Vue Hook Error:', error, info);

    if (shouldShowErrorPage(error)) {
      showError({
        statusCode: 500,
        statusMessage: 'Application Error',
        message: import.meta.dev ? error.message : 'Something went wrong',
        fatal: true,
      });
    }
  });

  // Handle unhandled promise rejections
  if (process.client) {
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled Promise Rejection:', event.reason);

      // Don't prevent default unless we're showing an error page
      if (shouldShowErrorPage(event.reason)) {
        event.preventDefault();
        showError({
          statusCode: 500,
          statusMessage: 'Request Failed',
          message: import.meta.dev
            ? String(event.reason)
            : 'Something went wrong',
          fatal: true,
        });
      }
    });

    // Handle JavaScript errors
    window.addEventListener('error', (event) => {
      console.error('JavaScript Error:', event.error);

      // Only handle truly fatal errors, let browser handle others
      if (event.error && shouldShowErrorPage(event.error)) {
        event.preventDefault();
        showError({
          statusCode: 500,
          statusMessage: 'Critical Error',
          message: import.meta.dev
            ? event.error.message
            : 'Something went wrong',
          fatal: true,
        });
      }
    });
  }
});
